export type DashboardCommissionKind = "super";

const DEFAULT_DASHBOARD_COMMISSION_PERCENT = 2.5;
const MIN_DASHBOARD_COMMISSION_PERCENT = 0;
const MAX_DASHBOARD_COMMISSION_PERCENT = 100;
const COMMISSION_PERCENT_PATTERN = /^\d{1,3}(?:[.,]\d{1,2})?$/;

function commissionStorageKey(userID: string) {
  return `dashboard.commissionRatePercent:${userID}`;
}

const dashboardStorage = (): Storage | null => {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
};

export function parseDashboardCommissionPercent(rawValue: string) {
  const trimmed = rawValue.trim();
  if (!COMMISSION_PERCENT_PATTERN.test(trimmed)) return null;

  const value = Number(trimmed.replace(",", "."));
  return Number.isFinite(value) &&
    value >= MIN_DASHBOARD_COMMISSION_PERCENT &&
    value <= MAX_DASHBOARD_COMMISSION_PERCENT
    ? value
    : null;
}

function normalizeCommissionPercent(value: number): number | null {
  if (!Number.isFinite(value)) return null;
  return parseDashboardCommissionPercent(String(value));
}

export function readDashboardCommissionPercent(userID: string | null): number {
  const storage = dashboardStorage();
  if (!userID || !storage) {
    return DEFAULT_DASHBOARD_COMMISSION_PERCENT;
  }

  try {
    const stored = storage.getItem(commissionStorageKey(userID));
    if (stored !== null) {
      const parsed = parseDashboardCommissionPercent(stored);
      if (parsed !== null) return parsed;
    }
  } catch {
    return DEFAULT_DASHBOARD_COMMISSION_PERCENT;
  }
  return DEFAULT_DASHBOARD_COMMISSION_PERCENT;
}

export function writeDashboardCommissionPercent(
  userID: string,
  percent: number,
): number {
  const normalized =
    normalizeCommissionPercent(percent) ?? DEFAULT_DASHBOARD_COMMISSION_PERCENT;
  const storage = dashboardStorage();
  try {
    storage?.setItem(commissionStorageKey(userID), String(normalized));
  } catch {
    // Simulation remains usable even when storage is blocked or full.
  }
  return normalized;
}

export function calculateDashboardCommissionCents(
  valueCents: number,
  percent: number,
): number {
  if (!Number.isSafeInteger(valueCents) || valueCents < 0) {
    throw new RangeError(
      "dashboard commission value must be nonnegative integer cents",
    );
  }
  const normalized = normalizeCommissionPercent(percent);
  if (normalized === null) {
    throw new RangeError("dashboard commission percent is invalid");
  }

  const basisPoints = BigInt(Math.round(normalized * 100));
  const roundedCents = (BigInt(valueCents) * basisPoints + 5_000n) / 10_000n;
  const result = Number(roundedCents);
  if (!Number.isSafeInteger(result)) {
    throw new RangeError("dashboard commission result exceeds safe cents");
  }
  return result;
}

export const DASHBOARD_COMMISSION_RATES: Record<
  DashboardCommissionKind,
  number
> = {
  super: 0.025,
};

export function getDashboardCommissionRate(kind: DashboardCommissionKind) {
  return DASHBOARD_COMMISSION_RATES[kind];
}

export function formatDashboardCommissionRate(
  kindOrRate: DashboardCommissionKind | number,
) {
  const rate =
    typeof kindOrRate === "number"
      ? kindOrRate
      : getDashboardCommissionRate(kindOrRate);

  return new Intl.NumberFormat("pt-BR", {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
    style: "percent",
  }).format(rate);
}

export function calculateDashboardCommission(
  value: number,
  kind: DashboardCommissionKind,
) {
  return value * getDashboardCommissionRate(kind);
}
